import { useCallback } from "react";
import {
  Alert,
  Button,
  Center,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { SlidePrintRoutingSettingsForm } from "../../../components/admin/bridge/SlidePrintRoutingSettingsForm";
import {
  useSlidePrintRoutingSettings,
  useUpdateSlidePrintRoutingSettings,
} from "../../../hooks/admin/useSlidePrintRoutingSettings";
import { useHasPermission } from "../../../hooks/useHasPermission";
import type { SlidePrintRoutingMode } from "../../../services/slidePrintRouting.service";

export default function SlidePrintRoutingSettingsAdminPage() {
  const allowed = useHasPermission("slide_print_settings.manage");
  const query = useSlidePrintRoutingSettings(allowed);
  const update = useUpdateSlidePrintRoutingSettings();
  const settings = query.data;
  const refetchSettings = query.refetch;
  const updateSettings = update.mutate;
  const handleRetry = useCallback(
    () => void refetchSettings(),
    [refetchSettings]
  );
  const handleSave = useCallback(
    (mode: SlidePrintRoutingMode) => {
      if (!settings) return;
      updateSettings({ mode, expectedUpdatedAt: settings.updatedAt });
    },
    [settings, updateSettings]
  );

  if (!allowed) {
    return (
      <Alert
        icon={<IconAlertCircle />}
        color="red"
        variant="light"
        title="Permission required"
        role="alert"
      >
        Only an authorized administrator or lab manager can change slide
        printing.
      </Alert>
    );
  }
  if (query.isLoading) {
    return (
      <Center mih={320}>
        <Loader />
      </Center>
    );
  }
  if (query.error || !settings) {
    return (
      <Alert
        icon={<IconAlertCircle />}
        color="red"
        variant="light"
        title="Slide printing settings unavailable"
        role="alert"
      >
        The current route could not be loaded.
        <Button mt="sm" onClick={handleRetry}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <div>
        <Title order={1}>Slide Printing</Title>
        <Text c="dimmed" mt="xs">
          Control how cutting workflow slide labels are sent to the Windows
          bridge.
        </Text>
      </div>
      <SlidePrintRoutingSettingsForm
        key={settings.updatedAt || settings.mode}
        currentMode={settings.mode}
        updatedAt={settings.updatedAt}
        saving={update.isPending}
        saveError={update.error?.message}
        onSave={handleSave}
      />
    </Stack>
  );
}
